import Head from 'next/head';

import { applicationSettings } from '@/constants';
import ArticleCards from '@/components/ArticleCards';
import ArticleCardsWithImage from '@/components/ArticleCardsWithImage';
import CategoryDudeImage from '@/components/CategoryDudeImage';
import Footer from '@/components/Footer';
import Header from '@/components/Header';
import HeroCategory from '@/components/HeroCategory';
import HeroCategoryCaseyDark from '@/components/HeroCategoryCaseyDark';
import HeroCategoryIVRAltDark from '@/components/HeroCategoryIVRAltDark';
import HeroCategoryLight from '@/components/HeroCategoryLight';
import Pagination from '@/components/Pagination';
import PopularCategoriesStatic from '@/components/PopularCategoriesStatic';
import StickyPhoneBubble from '@/components/StickyPhoneBubble';
import { CategoryCopy, hasAdditionalContent } from '@/components/CategoryCopy';
import { getArticlesByPage, getDailyCount } from '@/helpers/helpers_data';
import { CATEGORY_SUBSETS } from '@/contexts/categories_subset';

const { CALLAPRO_WEBSITE_URL } = applicationSettings;

const ARTICLES_PER_PAGE = 10;
const PAGE = 1;

export async function getStaticPaths() {
  const paths = CATEGORY_SUBSETS.map((category) => {
    return {
      params: {
        category: category.url_slug
      }
    };
  });

  return { paths, fallback: false };
}

export async function getStaticProps({ params }) {
  const category = CATEGORY_SUBSETS.find((category) => category.url_slug === params.category);
  const categoryId = category?.id;
  // Need to convert the category url_slug into the friend name to retrieve articles from the blog API.
  const categoryName = category?.name;
  const articlesCategoryName = category?.articles_category_name;

  const data = await getArticlesByPage({
    limit: ARTICLES_PER_PAGE,
    category: articlesCategoryName || categoryName
  });

  const daily_count = categoryId ? await getDailyCount(categoryId.toString()) : undefined;

  return {
    props: {
      category,
      articles: data.articles,
      total_articles: data.total,
      daily_count: daily_count
    }
  };
}

const getHero = (props) => {
  if (props.category?.use_caseys_theme) return <HeroCategoryCaseyDark {...props} />;
  if (props.category?.use_dark_theme) return <HeroCategoryIVRAltDark {...props} />;
  if (props.category?.use_light_theme_landing_layout) return <HeroCategoryLight {...props} />;
  return <HeroCategory {...props} />;
};

const Category = (props) => (
  <div className="page-wrapper">
    <Head>
      <title>{props.category?.meta?.title || 'Find Home Service Pros Near You'}</title>

      <meta name="description" content={props.category?.meta?.description || 'CallAPro - Find a Home Service Professional Near You'} />

      {props.category?.meta?.keywords && <meta name="keywords" content={props.category?.meta.keywords} />}
      {props.category?.meta?.og_description && <meta name="og:description" content={props.category?.meta.og_description} />}
      {props.category?.meta?.og_title && <meta name="og:title" content={props.category?.meta.og_title} />}
      {props.category?.meta?.og_url && <meta name="og:url" content={props.category?.meta.og_url} />}

      <link rel="canonical" href={props.category?.meta?.canonical || `${CALLAPRO_WEBSITE_URL}/${props.category.url_slug}/`} />
    </Head>

    <Header type="category" category={props.category} />
    {getHero(props)}

    {/* IF HAS EXTRA CONTENT, HIDE POPULAR CATEGORIES */}
    {!props.category?.hide_popular_categories && <PopularCategoriesStatic />}

    {props.category?.category_dude_image && <CategoryDudeImage category={props.category} />}

    {hasAdditionalContent(props.category.url_slug) && <CategoryCopy slug={props.category.url_slug} withImage={Boolean(props.category.category_dude_image)} />}

    {!props.category?.use_caseys_theme && props.articles.length > 0 && (
      <ArticleCardsWithImage articles={props.articles.slice(0, 3)} headline={`Recent ${props.category.articles_category_name_singular || props.category.name_singular} Articles`} />
    )}
    {!props.category?.use_caseys_theme && props.articles.length > 3 && (
      <ArticleCards articles={props.articles.slice(3, 10)} headline={`More ${props.category.articles_category_name_singular || props.category.name_singular} Articles`} />
    )}
    {!props.category?.use_caseys_theme && (
      <Pagination
        totalPages={props.total_articles <= ARTICLES_PER_PAGE ? 1 : Math.ceil((props.total_articles - ARTICLES_PER_PAGE) / ARTICLES_PER_PAGE + 1)}
        page={PAGE}
        path={`/${props.category.articles_url_slug || props.category.url_slug}`}
      />
    )}
    <Footer />
    <StickyPhoneBubble />
  </div>
);

export default Category;
